import { useEffect } from 'react';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface DeleteConfirmModalProps {
  funcionario: Funcionario;
  onConfirm: () => void;
  onClose: () => void;
}

export default function DeleteConfirmModal({ funcionario, onConfirm, onClose }: DeleteConfirmModalProps) {
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  }, [handleClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/40 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-sm p-6 flex flex-col gap-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto">
          <i className="ri-delete-bin-line text-red-500 text-xl" />
        </div>
        <div className="text-center">
          <h3 className="font-bold text-slate-800 text-base">Eliminar Funcionário</h3>
          <p className="text-sm text-slate-500 mt-1">
            Tem a certeza que pretende eliminar <strong className="text-slate-700">{funcionario.nome}</strong>?
            Esta ação não pode ser revertida.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="whitespace-nowrap flex-1 py-2.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="whitespace-nowrap flex-1 py-2.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
          >
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}
